// mission-initializer-ids.ts
//
// Pure id-derivation helpers for mission-initializer.ts (split out to keep
// that module ≤200 lines).
//
// Idempotency key (documented judgment call in evidence): InitRequestSchema
// has NO explicit operation_id field. deriveOperationId derives one
// deterministically as sha256 of the canonical (sorted-key) JSON of the
// VALIDATED request — same request content (incl. same blueprint hash/repo/
// base) => same operation id => a killed-and-rerun init recognizes the
// mission it already started instead of creating a second worktree/mission
// (tests 10/15).

import { createHash } from "node:crypto";
import type { InitRequest } from "./schemas/init-schema.js";

/** sha256 of the canonical (sorted-key) JSON of the validated request — the idempotency key. */
export function deriveOperationId(request: InitRequest): string {
  const canonical = JSON.stringify(sortDeep(request));
  return `init-${createHash("sha256").update(canonical, "utf8").digest("hex").slice(0, 16)}`;
}

function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = sortDeep((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

export function deriveMissionId(operationId: string, now: () => Date): string {
  const year = now().getUTCFullYear();
  return `ME-${year}-${operationId.slice(5, 11)}`;
}
